import { useMemo } from "react";
import {
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseSuspenseQueryOptions,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import type {
  ConnectionId,
  Identity,
  InferTypeOfRow,
  ProcedureParamsType,
  ProcedureReturnType,
  Query,
  ScheduleAt,
  TimeDuration,
  Timestamp,
  TypedTableDef,
} from "spacetimedb";
// oxlint-disable-next-line eslint/no-restricted-imports - `useProcedure` lives in spacetimedb/react (not re-exported from tanstack); this module is the allowed wrapper.
import { useProcedure } from "spacetimedb/react";
// oxlint-disable-next-line eslint/no-restricted-imports - we use the `useReducer` function from `spacetimedb/tanstack` to wrap the reducer in a TanStack `useMutation` hook
import { spacetimeDBQuery, useReducer } from "spacetimedb/tanstack";

import { type procedures, type reducers } from "@tycho/web-bindings";

import {
  notifyReadOnlyBlocked,
  ReadOnlyMutationError,
  useIsReadOnly,
} from "@/web/lib/readOnlyMutations";

// The blocked callables close over nothing, so they are defined once at module
// scope instead of per render.
//
// That is load-bearing rather than tidiness. These stand in for react-query's
// `mutate` / `mutateAsync`, which ARE referentially stable, and callers put
// them in `useCallback` / `useMemo` dependency arrays on that assumption. A
// fresh closure per render silently invalidates every such memo downstream. In
// the orientation tour that chain runs `mutateAsync` → `finishTour` → `next` →
// `completeStep` → the tour's context value → every anchor tile re-renders →
// their callback refs re-run → tour state updates → render again, forever.
// `/admin/smb/$brandId` died with "Maximum update depth exceeded" for exactly
// that reason, and only there, because `isReadOnly` is false everywhere else.
function blockedMutate(): void {
  notifyReadOnlyBlocked();
}

function blockedMutateAsync(): Promise<never> {
  notifyReadOnlyBlocked();
  return Promise.reject(new ReadOnlyMutationError());
}

/**
 * When the surrounding subtree is read-only (the admin SMB viewer), swap the
 * mutation's `mutate` / `mutateAsync` for no-ops that toast instead of hitting
 * the server. We override the *returned* callables rather than the
 * `mutationFn`, so react-query's `onSuccess` / `onError` lifecycle never runs —
 * a blocked write leaves zero trace beyond the toast. Outside the viewer
 * (`isReadOnly === false`) the mutation is returned untouched.
 *
 * A hook rather than a plain function so the guarded result is memoized: the
 * object it returns is spread from `mutation`, and re-creating it every render
 * would hand callers a moving identity where the unguarded path gives a stable
 * one. Read-only must not change a mutation's referential behaviour — only
 * what its callables do.
 */
function useReadOnlyGuard<TData, TError, TVars>(
  mutation: UseMutationResult<TData, TError, TVars>,
  isReadOnly: boolean,
): UseMutationResult<TData, TError, TVars> {
  return useMemo(() => {
    if (!isReadOnly) return mutation;
    return {
      ...mutation,
      mutate: blockedMutate as UseMutationResult<TData, TError, TVars>["mutate"],
      mutateAsync: blockedMutateAsync as UseMutationResult<TData, TError, TVars>["mutateAsync"],
    };
  }, [mutation, isReadOnly]);
}

type DoNotPrettify = Identity | ConnectionId | Timestamp | TimeDuration | ScheduleAt;

/**
 * Utility to make TS show cleaner types by flattening intersections.
 */
type Prettify<T> = T extends DoNotPrettify ? T : { [K in keyof T]: T[K] } & {};

type IsEmptyObject<T> = [keyof T] extends [never] ? true : false;
type MaybeParams<T> = IsEmptyObject<T> extends true ? [] : [params: T];

type ReducerMap = typeof reducers;
type ReducerKey = keyof ReducerMap;
type ReducerDef = ReducerMap[ReducerKey];

type ReducerParams<R extends ReducerDef> = MaybeParams<Prettify<InferTypeOfRow<R["params"]>>>;

type ReducerVariables<R extends ReducerDef> =
  ReducerParams<R> extends [] ? void : ReducerParams<R>[0];

/**
 * Wraps a SpacetimeDB reducer in a TanStack `useMutation` hook.
 *
 * No-param reducers use `void` variables so `mutate()` can be called with no
 * args. The returned object is a standard TanStack mutation result with
 * `mutateAsync`, `isPending`, `error`, etc.
 *
 * @param reducer - A reducer definition from `module_bindings/reducers`.
 * @param extraOptions - Additional `UseMutationOptions` (e.g. `onSuccess`, `onError`).
 *
 * @example
 * ```ts
 * const { mutateAsync: createSmb, isPending } = useReducerMutation(reducers.createSmb);
 * await createSmb();
 * ```
 *
 * @example
 * ```ts
 * const { mutateAsync: selectBusiness } = useReducerMutation(reducers.selectBusinessFromPlace);
 * await selectBusiness({ placeId: "abc123" });
 * ```
 */
export function useReducerMutation<R extends ReducerDef>(
  reducer: R,
  extraOptions: Omit<UseMutationOptions<void, Error, ReducerVariables<R>>, "mutationFn"> = {},
) {
  const call = useReducer(reducer) as (...args: unknown[]) => Promise<void>;
  const isReadOnly = useIsReadOnly();

  const mutation = useMutation<void, Error, ReducerVariables<R>>({
    mutationFn: (variables) => (variables === undefined ? call() : call(variables)),
    ...extraOptions,
  });
  return useReadOnlyGuard(mutation, isReadOnly);
}

type ProcedureMap = typeof procedures;
type ProcedureDef = ProcedureMap[keyof ProcedureMap];

type ProcedureVariables<P extends ProcedureDef> =
  ProcedureParamsType<P> extends [] ? void : ProcedureParamsType<P>[0];

/**
 * Wraps a SpacetimeDB procedure (RPC-style call that returns a value) in a
 * TanStack `useMutation` hook.
 *
 * Uses `useProcedure` from `spacetimedb/react` (connection queueing, same idea
 * as `useReducer`) and mirrors {@link useReducerMutation}: pass a procedure
 * definition from `module_bindings/procedures`, not a string name.
 *
 * @param procedure - A procedure definition from `module_bindings/procedures`.
 * @param extraOptions - Additional `UseMutationOptions` (e.g. `onSuccess`, `onError`).
 *
 * @example
 * ```ts
 * const { mutateAsync: createUploadUrl } = useProcedureMutation(procedures.createHeroUploadUrl);
 * const { url } = await createUploadUrl({ fileName: "hero.png" });
 * ```
 */
export function useProcedureMutation<P extends ProcedureDef>(
  procedure: P,
  extraOptions: Omit<
    UseMutationOptions<ProcedureReturnType<P>, Error, ProcedureVariables<P>>,
    "mutationFn"
  > = {},
) {
  const call = useProcedure(procedure) as (...args: unknown[]) => Promise<ProcedureReturnType<P>>;
  const isReadOnly = useIsReadOnly();

  const mutation = useMutation<ProcedureReturnType<P>, Error, ProcedureVariables<P>>({
    mutationFn: (variables) => (variables === undefined ? call() : call(variables)),
    ...extraOptions,
  });
  return useReadOnlyGuard(mutation, isReadOnly);
}

/**
 * Suspense-backed query for a SpacetimeDB procedure.
 *
 * Use this for admin / read-side procedures that return a value but don't have
 * a backing subscription table. Wraps `useProcedure` (which abstracts the
 * SpacetimeDB connection) so callers don't have to reach for `useSpacetimeDB`
 * or `getConnection` directly.
 *
 * @example
 * ```ts
 * const { data: brandsResult } = useProcedureQuery(procedures.adminListBrands, {});
 * brandsResult.brands.forEach(...)
 * ```
 */
export function useProcedureQuery<
  P extends ProcedureDef,
  V extends ProcedureVariables<P> | undefined = ProcedureVariables<P>,
>(
  procedure: P,
  variables?: V,
  extraOptions: Omit<
    UseSuspenseQueryOptions<ProcedureReturnType<P>, Error>,
    "queryFn" | "queryKey"
  > = {},
) {
  const call = useProcedure(procedure) as (...args: unknown[]) => Promise<ProcedureReturnType<P>>;
  const procedureName = procedure.accessorName;

  return useSuspenseQuery<ProcedureReturnType<P>, Error>({
    queryKey: ["procedure", procedureName, variables] as const,
    queryFn: () => (variables === undefined ? call() : call(variables)),
    ...extraOptions,
  });
}

/**
 * Non-suspense variant of {@link useProcedureQuery}. Use when the calling
 * component wants explicit loading / error states (e.g. inside a drawer body
 * where a Suspense fallback would hide the surrounding form controls) or
 * when the procedure can fail in ways the caller wants to recover from
 * locally rather than throwing to the nearest error boundary.
 *
 * Shares the same `["procedure", name, variables]` cache key as the suspense
 * variant, so a later `<Suspense>`-wrapped caller reading the same row
 * resolves synchronously.
 *
 * @example
 * ```ts
 * // Render the count line if the row loaded, fall back to a placeholder
 * // while the procedure is in flight or after a recoverable error.
 * const { data: row } = useProcedureQueryLazy(procedures.getShareableBusinessCard, {
 *   cardId,
 *   accessCode,
 * });
 * ```
 */
export function useProcedureQueryLazy<
  P extends ProcedureDef,
  V extends ProcedureVariables<P> | undefined = ProcedureVariables<P>,
>(
  procedure: P,
  variables?: V,
  extraOptions: Omit<UseQueryOptions<ProcedureReturnType<P>, Error>, "queryFn" | "queryKey"> = {},
) {
  const call = useProcedure(procedure) as (...args: unknown[]) => Promise<ProcedureReturnType<P>>;
  const procedureName = procedure.accessorName;

  return useQuery<ProcedureReturnType<P>, Error>({
    queryKey: ["procedure", procedureName, variables] as const,
    queryFn: () => (variables === undefined ? call() : call(variables)),
    ...extraOptions,
  });
}

// Given a type T, forces any keys in K to be required (i.e., not optional).
type EnsureKeyNoMissing<T, K extends keyof T> = Omit<T, K> & {
  [P in K]-?: T[P];
};

type AnyQuery = Query<TypedTableDef>;
type RowFromQuery<Q extends AnyQuery> = NonNullable<
  Q extends { readonly __row?: infer Row } ? Row : never
>;
type TableQueryOptions<Q extends AnyQuery | "skip"> = Q extends "skip"
  ? UseQueryOptions<never[], Error>
  : EnsureKeyNoMissing<
      UseSuspenseQueryOptions<RowFromQuery<Extract<Q, AnyQuery>>[], Error>,
      "queryFn"
    >;

export function tableQueryOptions<Q extends AnyQuery | "skip">(
  queryOrSkip: Q,
): TableQueryOptions<Q>;

/**
 * Builds a TanStack Query options object for a SpacetimeDB table query.
 *
 * Pass the result to `useQuery`, `useSuspenseQuery`, or any TanStack helper
 * that accepts query options. The returned rows are typed to the table's
 * column schema.
 *
 * Pass `"skip"` instead of a query to disable fetching until a precondition
 * is met (equivalent to `enabled: false`).
 *
 * @param queryOrSkip - A SpacetimeDB `Query<T>` or the literal `"skip"`.
 *
 * @example
 * ```ts
 * const { data } = useSuspenseQuery(tableQueryOptions(tables.smbOnboarding));
 * const row = data[0] ?? null;
 * ```
 *
 * @example
 * ```ts
 * // Conditionally skip the query
 * const opts = ready
 *   ? tableQueryOptions(tables.currentUser)
 *   : tableQueryOptions("skip");
 * const { data } = useQuery(opts);
 * ```
 */
export function tableQueryOptions<Q extends AnyQuery | "skip">(queryOrSkip: Q) {
  const queryOptions =
    queryOrSkip === "skip" ? spacetimeDBQuery("skip") : spacetimeDBQuery(queryOrSkip);

  return {
    ...queryOptions,
  } as unknown as TableQueryOptions<Q>;
}
